import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

const { Admin, ITAdmin, ContractSpecialist, FacilitiesAdmin, Accounting, Controller, CFO, VPApprover, ServiceDesk } = UserRole;

interface NavItem {
  label: string;
  to: string;
  roles?: UserRole[];
}

interface NavSection {
  title: string;
  items: NavItem[];
  roles?: UserRole[];
}

const sections: NavSection[] = [
  {
    title: 'Main',
    items: [
      { label: 'Dashboard', to: '/' },
      { label: 'Ask Nexus', to: '/ask' },
      { label: 'Vendors', to: '/vendors' },
      { label: 'Risk Alerts', to: '/vendors/risk-alerts', roles: [Admin, ITAdmin, ServiceDesk] },
      { label: 'Assets', to: '/assets', roles: [Admin, ITAdmin, ContractSpecialist, Controller, CFO, ServiceDesk] },
      { label: 'Circuits', to: '/circuits', roles: [Admin, ITAdmin, ContractSpecialist, ServiceDesk] },
      { label: 'Sites', to: '/sites' },
      { label: 'Employees', to: '/employees' },
      { label: 'Data Validation', to: '/admin/data-validation', roles: [Admin, ITAdmin, ServiceDesk] },
      { label: 'Service Desk', to: '/service-desk', roles: [Admin, ITAdmin, ContractSpecialist, ServiceDesk] },
    ],
  },
  {
    title: 'Analytics',
    roles: [Admin],
    items: [
      { label: 'Reports', to: '/reports/financial', roles: [Admin, Accounting, Controller, CFO] },
      { label: 'IT Spend', to: '/reports/it-spend', roles: [Admin, Controller, CFO] },
      { label: 'Spend Analytics', to: '/reports/spend-analytics', roles: [Admin, Controller, CFO] },
      { label: 'Invoices', to: '/invoices', roles: [Admin, ContractSpecialist, FacilitiesAdmin, Accounting, Controller, CFO, VPApprover] },
      { label: 'Budget', to: '/budget', roles: [Admin] },
      { label: 'Budget vs. Actual', to: '/budget/vs-actual', roles: [Admin, Controller, CFO] },
    ],
  },
  {
    title: 'Admin',
    roles: [Admin, ITAdmin],
    items: [
      { label: 'Users', to: '/admin/users', roles: [Admin] },
      { label: 'Data Import', to: '/admin/import', roles: [Admin, ITAdmin, Controller, CFO] },
      { label: 'Credentials', to: '/admin/credentials', roles: [Admin, ITAdmin] },
      { label: 'Health', to: '/admin/health', roles: [Admin, ITAdmin] },
      { label: 'Settings', to: '/admin/settings', roles: [Admin, ITAdmin] },
      { label: 'Audit Log', to: '/admin/audit', roles: [Admin, ITAdmin, ContractSpecialist] },
      { label: 'Error Log', to: '/admin/errors', roles: [Admin, ITAdmin] },
      { label: 'Login Audit', to: '/admin/login-audit', roles: [Admin, ITAdmin] },
      { label: 'Scheduled Reports', to: '/admin/scheduled-reports', roles: [Admin, ITAdmin, ContractSpecialist, FacilitiesAdmin, Accounting, Controller, CFO, VPApprover] },
      { label: 'API Rates', to: '/admin/api-rates', roles: [Admin, ITAdmin] },
    ],
  },
];

export function Sidebar() {
  const { hasAnyRole } = useAuth();

  function isVisible(roles?: UserRole[]): boolean {
    if (!roles || roles.length === 0) return true;
    return hasAnyRole(roles);
  }

  return (
    <aside className="w-[220px] min-w-[220px] bg-baker-dark text-white py-5 overflow-y-auto">
      <nav>
        {sections.map((section) => {
          if (!isVisible(section.roles)) return null;

          const visibleItems = section.items.filter((item) => isVisible(item.roles));
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className="mb-5">
              <div className="px-5 mb-2 text-[0.65rem] font-semibold uppercase tracking-widest text-white/30">
                {section.title}
              </div>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center px-5 py-1.5 text-[13px] transition-colors ${
                      isActive
                        ? 'text-white font-medium border-l-2 border-baker-accent bg-white/[0.04]'
                        : 'text-white/60 hover:bg-white/[0.05] hover:text-white/90 border-l-2 border-transparent'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
